// =======================
// src/paginas/modelos/componentes/modalGerenciadorCatalogos.js
// =======================

import React, { useEffect, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";

import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { api } from "@api/api";
import { ajustarTamanhoFonteSre } from "../../../estilos/global";

const ABAS = {
  CATEGORIAS: "categorias",
  ASSUNTOS: "assuntos",
  ETAPAS_MODALIDADES: "etapas-modalidades",
};

const ModalGerenciarCatalogos = ({
  open,
  onClose,
  categorias,
  assuntos,
  etapasModalidades,
  onAlteracao,
}) => {
  const [aba, setAba] = useState(ABAS.CATEGORIAS);

  const [nome, setNome] = useState("");

  const [itemEditando, setItemEditando] = useState(null);
  const [nomeEdicao, setNomeEdicao] = useState("");
  const [itemParaExcluir, setItemParaExcluir] = useState(null);

  const [salvando, setSalvando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);

  const [erro, setErro] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }

    setAba(ABAS.CATEGORIAS);
    setNome("");
    setItemEditando(null);
    setNomeEdicao("");
    setItemParaExcluir(null);
    setErro("");
  }, [open]);

  const itens = useMemo(() => {
    if (aba === ABAS.CATEGORIAS) {
      return categorias;
    }

    if (aba === ABAS.ASSUNTOS) {
      return assuntos;
    }

    return etapasModalidades;
  }, [aba, categorias, assuntos, etapasModalidades]);

  const configuracaoAba = useMemo(() => {
    if (aba === ABAS.CATEGORIAS) {
      return {
        tituloColuna: "Categoria",
        labelCampo: "Nome da categoria",
        placeholder: "Ex.: Escola",
        vazio: "Nenhuma categoria cadastrada.",
        informacao: "As categorias são usadas para classificar os modelos.",
      };
    }

    if (aba === ABAS.ASSUNTOS) {
      return {
        tituloColuna: "Assunto",
        labelCampo: "Nome do assunto",
        placeholder: "Ex.: Autorização de funcionamento",
        vazio: "Nenhum assunto cadastrado.",
        informacao: "Os assuntos complementam a classificação dos modelos.",
      };
    }

    return {
      tituloColuna: "Etapa / Modalidade",
      labelCampo: undefined,
      placeholder: "Nome da etapa/modalidade",
      vazio: "Nenhuma etapa ou modalidade cadastrada.",
      informacao:
        "Cadastre as etapas de ensino e modalidades que poderão ser selecionadas nos modelos.",
    };
  }, [aba]);

  const lerMensagemErro = async (response, mensagemPadrao) => {
    const texto = await response.text();

    if (!texto) {
      return mensagemPadrao;
    }

    try {
      const dados = JSON.parse(texto);

      if (Array.isArray(dados?.message)) {
        return dados.message.join(" ");
      }

      return dados?.message || mensagemPadrao;
    } catch {
      return texto || mensagemPadrao;
    }
  };

  const limparCadastro = () => {
    setNome("");
    setErro("");
  };

  const cancelarEdicao = () => {
    setItemEditando(null);
    setNomeEdicao("");
    setErro("");
  };

  const limparFormulario = () => {
    limparCadastro();
    cancelarEdicao();
  };

  const handleFechar = (_event, reason) => {
    if (salvando || excluindo) {
      return;
    }

    if (reason === "escapeKeyDown" && itemEditando) {
      cancelarEdicao();
      return;
    }

    limparFormulario();
    setItemParaExcluir(null);
    onClose();
  };

  const handleTrocarAba = (_event, novaAba) => {
    setAba(novaAba);
    limparFormulario();
    setItemParaExcluir(null);
  };

  const handleEditar = (item) => {
    setItemEditando(item);
    setNomeEdicao(item.nome ?? "");
    setErro("");
  };

  const obterUrlSalvar = (editando, id) => {
    if (aba === ABAS.CATEGORIAS) {
      return editando ? api.updateModelCategory(id) : api.createModelCategory();
    }

    if (aba === ABAS.ASSUNTOS) {
      return editando ? api.updateModelSubject(id) : api.createModelSubject();
    }

    return editando
      ? api.updateModelStageModality(id)
      : api.createModelStageModality();
  };

  const obterUrlExcluir = (id) => {
    if (aba === ABAS.CATEGORIAS) {
      return api.deleteModelCategory(id);
    }

    if (aba === ABAS.ASSUNTOS) {
      return api.deleteModelSubject(id);
    }

    return api.deleteModelStageModality(id);
  };

  const salvarItem = async ({ item = null, nomeValor }) => {
    if (salvando || excluindo) {
      return;
    }

    const nomeLimpo = nomeValor.trim();

    if (!nomeLimpo) {
      setErro(
        `Informe ${
          aba === ABAS.CATEGORIAS
            ? "o nome da categoria"
            : aba === ABAS.ASSUNTOS
              ? "o nome do assunto"
              : "o nome da etapa ou modalidade"
        }.`,
      );

      return;
    }

    if (nomeLimpo.length > 100) {
      setErro("O nome deve possuir no máximo 100 caracteres.");

      return;
    }

    const editando = Boolean(item);

    setSalvando(true);
    setErro("");

    try {
      const token = localStorage.getItem("token");

      const body = { nome: nomeLimpo };

      const response = await fetch(obterUrlSalvar(editando, item?.id), {
        method: editando ? "PATCH" : "POST",

        headers: {
          Authorization: token ? `Bearer ${token}` : "",

          "Content-Type": "application/json",
        },

        credentials: "include",

        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(
          await lerMensagemErro(response, "Não foi possível salvar."),
        );
      }

      await onAlteracao();

      if (editando) {
        cancelarEdicao();
      } else {
        limparCadastro();
      }
    } catch (error) {
      console.error("Erro ao salvar catálogo:", error);

      setErro(error.message || "Não foi possível salvar.");
    } finally {
      setSalvando(false);
    }
  };

  const handleAdicionar = () => {
    salvarItem({ nomeValor: nome });
  };

  const handleSalvarEdicao = () => {
    if (!itemEditando) {
      return;
    }

    salvarItem({ item: itemEditando, nomeValor: nomeEdicao });
  };

  const handleKeyDownCadastro = (event) => {
    if (event.key === "Enter" && !event.nativeEvent.isComposing) {
      event.preventDefault();
      handleAdicionar();
    }
  };

  const handleKeyDownEdicao = (event) => {
    if (event.key === "Enter" && !event.nativeEvent.isComposing) {
      event.preventDefault();
      event.stopPropagation();
      handleSalvarEdicao();
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();

      if (!salvando && !excluindo) {
        cancelarEdicao();
      }
    }
  };

  const handleExcluir = async () => {
    if (!itemParaExcluir || excluindo) {
      return;
    }

    setExcluindo(true);
    setErro("");

    try {
      const token = localStorage.getItem("token");

      const idExcluido = itemParaExcluir.id;

      const response = await fetch(obterUrlExcluir(idExcluido), {
        method: "DELETE",

        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },

        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(
          await lerMensagemErro(response, "Não foi possível excluir."),
        );
      }

      setItemParaExcluir(null);

      await onAlteracao();

      if (itemEditando?.id === idExcluido) {
        cancelarEdicao();
      }
    } catch (error) {
      console.error("Erro ao excluir catálogo:", error);

      setItemParaExcluir(null);

      setErro(error.message || "Não foi possível excluir.");
    } finally {
      setExcluindo(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleFechar}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        {/* ===============================================
            CABEÇALHO
        =============================================== */}

        <DialogTitle
          sx={{
            px: 3,
            pt: 2.5,
            pb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  color: "#263238",
                  fontWeight: 700,
                }}
              >
                Gerenciar catálogos
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.25 }}
              >
                Cadastre e edite as opções usadas nos modelos.
              </Typography>
            </Box>

            <IconButton
              size="small"
              onClick={handleFechar}
              disabled={salvando || excluindo}
              aria-label="Fechar"
              sx={{
                color: "#607d8b",
              }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent
          sx={{
            px: 3,
            py: 2.5,
          }}
        >
          {/* ===============================================
              ABAS
          =============================================== */}

          <Box
            sx={{
              border: "1px solid #c9def4",
              borderRadius: 1.5,
              overflow: "hidden",
              mb: 2,
            }}
          >
            <Tabs
              value={aba}
              onChange={handleTrocarAba}
              variant="fullWidth"
              TabIndicatorProps={{
                sx: {
                  height: 3,
                  backgroundColor: "#087cf0",
                },
              }}
              sx={{
                minHeight: 44,

                "& .MuiTab-root": {
                  minHeight: 44,
                  textTransform: "none",
                  fontSize: ajustarTamanhoFonteSre("0.875rem"),
                  fontWeight: 600,
                  color: "#52606d",
                  backgroundColor: "#fff",
                  transition: "background-color 0.2s ease",
                },

                "& .MuiTab-root:not(:last-of-type)": {
                  borderRight: "1px solid #e2edf7",
                },

                "& .Mui-selected": {
                  color: "#0767d8 !important",
                  backgroundColor: "#eef6fd",
                },
              }}
            >
              <Tab value={ABAS.CATEGORIAS} label="Categorias" />

              <Tab value={ABAS.ASSUNTOS} label="Assuntos" />

              <Tab
                value={ABAS.ETAPAS_MODALIDADES}
                label="Etapas e modalidades"
              />
            </Tabs>
          </Box>

          {/* ===============================================
              INFORMAÇÃO
          =============================================== */}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              backgroundColor: "#eaf4ff",
              border: "1px solid #c9def4",
              borderRadius: 1.5,
              px: 1.5,
              py: 1.25,
              mb: 2,
            }}
          >
            <InfoOutlinedIcon
              fontSize="small"
              sx={{
                color: "#087cf0",
                flexShrink: 0,
              }}
            />

            <Typography
              variant="body2"
              sx={{
                color: "#455a64",
              }}
            >
              {configuracaoAba.informacao}
            </Typography>
          </Box>

          {erro && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {erro}
            </Alert>
          )}

          {/* ===============================================
              FORMULÁRIO
          =============================================== */}

          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",
                sm: "minmax(0, 1fr) auto",
              },

              gap: 1.25,
              alignItems: "start",
              mb: 2.5,
            }}
          >
            <TextField
              size="small"
              fullWidth
              label={configuracaoAba.labelCampo}
              placeholder={configuracaoAba.placeholder}
              value={nome}
              onChange={(event) => setNome(event.target.value)}
              onKeyDown={handleKeyDownCadastro}
              disabled={salvando}
              inputProps={{
                maxLength: 100,
                "aria-label":
                  configuracaoAba.labelCampo || configuracaoAba.placeholder,
              }}
            />

            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Button
                variant="contained"
                onClick={handleAdicionar}
                disabled={salvando}
                sx={{
                  minWidth: 100,
                  height: 40,
                  textTransform: "none",
                  boxShadow: "none",

                  "&:hover": {
                    boxShadow: "none",
                  },
                }}
              >
                {salvando ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  "Adicionar"
                )}
              </Button>
            </Box>
          </Box>

          {/* ===============================================
              LISTAGEM
          =============================================== */}

          {itens.length === 0 ? (
            <Box
              sx={{
                border: "1px solid #e2edf7",
                borderRadius: 1.5,
                py: 3,
                px: 2,
                textAlign: "center",
                backgroundColor: "#fafcfe",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {configuracaoAba.vazio}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                border: "1px solid #c9def4",
                borderRadius: 1.5,
                overflow: "hidden",
              }}
            >
              {/* CABEÇALHO DA LISTA */}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) 100px",
                  gap: 1,
                  alignItems: "center",
                  px: 2,
                  py: 1,
                  backgroundColor: "#eef6fd",
                  borderBottom: "1px solid #c9def4",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#263238",
                    fontWeight: 700,
                  }}
                >
                  {configuracaoAba.tituloColuna}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    color: "#263238",
                    fontWeight: 700,
                    textAlign: "center",
                  }}
                >
                  Ações
                </Typography>
              </Box>

              {/* ITENS */}

              {itens.map((item, index) => (
                <Box
                  key={item.id}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) 100px",
                    gap: 1,
                    alignItems: "center",
                    px: 2,
                    py: 1,
                    minHeight: 48,

                    borderTop: index === 0 ? "none" : "1px solid #e6eef5",

                    backgroundColor: "#fff",

                    "&:hover": {
                      backgroundColor: "#fbfdff",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minWidth: 0,
                    }}
                  >
                    {itemEditando?.id === item.id ? (
                      <TextField
                        size="small"
                        fullWidth
                        autoFocus
                        value={nomeEdicao}
                        onChange={(event) => setNomeEdicao(event.target.value)}
                        onKeyDown={handleKeyDownEdicao}
                        disabled={salvando}
                        inputProps={{
                          maxLength: 100,
                          "aria-label": `Editar ${configuracaoAba.tituloColuna}`,
                        }}
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        noWrap
                        title={item.nome}
                        sx={{
                          color: "#37474f",
                          minWidth: 0,
                        }}
                      >
                        {item.nome}
                      </Typography>
                    )}
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                    }}
                  >
                    {itemEditando?.id === item.id ? (
                      <>
                        <IconButton
                          size="small"
                          title="Salvar alteração"
                          onClick={handleSalvarEdicao}
                          disabled={salvando}
                          sx={{ color: "#087cf0" }}
                        >
                          <CheckRoundedIcon fontSize="small" />
                        </IconButton>

                        <IconButton
                          size="small"
                          title="Cancelar edição"
                          onClick={cancelarEdicao}
                          disabled={salvando}
                          sx={{ color: "#607d8b" }}
                        >
                          <CloseRoundedIcon fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton
                          size="small"
                          title="Editar"
                          onClick={() => handleEditar(item)}
                          disabled={salvando || excluindo}
                          sx={{ color: "#087cf0" }}
                        >
                          <EditRoundedIcon fontSize="small" />
                        </IconButton>

                        <IconButton
                          size="small"
                          title="Excluir"
                          onClick={() => {
                            setErro("");
                            setItemParaExcluir(item);
                          }}
                          disabled={salvando || excluindo}
                          sx={{ color: "#d32f2f" }}
                        >
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>

        <Divider />

        <DialogActions
          sx={{
            px: 3,
            py: 1.5,
          }}
        >
          <Button
            variant="text"
            onClick={handleFechar}
            disabled={salvando || excluindo}
            sx={{
              textTransform: "none",
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===============================================
          CONFIRMAÇÃO DE EXCLUSÃO
      =============================================== */}

      <Dialog
        open={Boolean(itemParaExcluir)}
        onClose={() => {
          if (!excluindo) {
            setItemParaExcluir(null);
          }
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
          }}
        >
          Excluir item
        </DialogTitle>

        <DialogContent>
          <Typography
            variant="body1"
            sx={{
              color: "#37474f",
            }}
          >
            Deseja realmente excluir <strong>{itemParaExcluir?.nome}</strong>?
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Se existirem modelos vinculados a este item, a exclusão será
            bloqueada.
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setItemParaExcluir(null)}
            disabled={excluindo}
            sx={{
              textTransform: "none",
            }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={handleExcluir}
            disabled={excluindo}
            sx={{
              textTransform: "none",
              boxShadow: "none",

              "&:hover": {
                boxShadow: "none",
              },
            }}
          >
            {excluindo ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              "Excluir"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ModalGerenciarCatalogos;
